import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TranslationContext } from '../../../../assets/contexts/translationContext';
import {
  getDepositSession,
  resumeDepositFlow,
  subscribeDepositSession,
} from '../../../../assets/services/deposit';
import CardPage from '../../../CardPages/CardPage/CardPage';
import ScreenHeader from '../../../ScreenHeader/ScreenHeader';
import BottomActionButton from '../../../shared/BottomActionButton/BottomActionButton';
import DepositOperations from '../DepositOperations/DepositOperations';
import './DepositConfirming.css';

function DepositConfirming() {
  const navigate = useNavigate();
  const location = useLocation();
  const { common, deposit } = useContext(TranslationContext);
  const [session, setSession] = useState(() => getDepositSession());
  const sessionRef = useRef(session);
  const sessionCardId = session?.cardId;
  const sessionAddressId = session?.addressId;
  const returnPath = location.state?.returnPath || session?.returnPath || '/home';
  const safeReturnPath = typeof returnPath === 'string' && returnPath && !returnPath.startsWith('/deposit')
    ? returnPath
    : '/home';

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const currentSession = sessionRef.current;

    if (currentSession?.status === 'success') {
      navigate('/deposit/success', { replace: true });
      return undefined;
    }

    if (currentSession?.status === 'failed') {
      navigate('/deposit/failed', { replace: true });
      return undefined;
    }

    if (currentSession?.status !== 'confirming') {
      navigate('/deposit', { replace: true });
      return undefined;
    }

    const unsubscribe = subscribeDepositSession((nextSession) => {
      setSession(nextSession);

      if (nextSession?.status === 'success') {
        navigate('/deposit/success', { replace: true });
      }

      if (nextSession?.status === 'failed') {
        navigate('/deposit/failed', { replace: true });
      }
    });

    if (sessionCardId && sessionAddressId) {
      resumeDepositFlow(currentSession);
    }

    return unsubscribe;
  }, [navigate, sessionAddressId, sessionCardId]);

  const operations = Array.isArray(session?.operations) ? session.operations : [];
  const amountLabel = `+ ${session?.amountAsset ?? `${session?.amountUsdt ?? '0'} ${session?.asset ?? 'USDT'}`}`;
  const handleDone = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }

    navigate(safeReturnPath, { replace: true });
  };

  return (
    <CardPage className="deposit-confirming">
      <div className="deposit-confirming__content">
        <ScreenHeader title={deposit.topUpCard} />

        <div className="deposit-confirming__hero">
          <div className="deposit-confirming__icon" aria-hidden="true">
            <span className="deposit-confirming__icon-gradient" />
            <span className="deposit-confirming__icon-wave deposit-confirming__icon-wave--one" />
            <span className="deposit-confirming__icon-wave deposit-confirming__icon-wave--two" />
            <span className="deposit-confirming__icon-dots">
              <span />
              <span />
              <span />
            </span>
          </div>
          <p className="deposit-confirming__amount">{amountLabel}</p>
          <p className="deposit-confirming__merchant">
            {deposit.viaNetwork.replace('{{network}}', session?.network ?? 'TRC-20')}
          </p>
          <span className="deposit-confirming__status">{deposit.confirming}</span>
        </div>

        <DepositOperations
          operations={operations}
          note={deposit.transferDetected}
          showEmpty={false}
        />
      </div>

      <BottomActionButton className="deposit-confirming__done" onClick={handleDone}>
        {common.done}
      </BottomActionButton>
    </CardPage>
  );
}

export default DepositConfirming;
